import React from 'react'
import { AppSvg, Card, FormattedHTMLMessage, icons, Button } from '@web/walter-ui-kit'
import cn from 'classnames'
import { KycRequestStatus, KycStatus } from '@web/walter-lib/api'
import { useIntl } from 'react-intl'

import styles from './style.module.css'
import { lang } from './lang'

interface Props {
    level: number
    isDisabled?: boolean
    isCurrent?: boolean
    onUpgrade?: VoidFunction
    isTokenLoading?: boolean
    loadingLevel?: number
    status?: KycStatus
    transition?: KycRequestStatus['transition']
}

type KycStatusMapping = {
    [key in Exclude<KycStatus, 'verified' | 'initial'>]: {
        svgName: string
        text: {
            id: string
            defaultMessage: string
        }
        colorStatus: 'warning' | 'error'
    }
}

export enum KycStatuses {
    pending = 'pending',
    rejected = 'rejected',
    information_requested = 'information_requested',
    verified = 'verified',
    initial = 'initial',
}

const kycStatusMapping: KycStatusMapping = {
    pending: {
        svgName: icons.ic12CircleExclamation,
        text: lang.pendingStatus,
        colorStatus: 'warning',
    },
    rejected: {
        svgName: icons.ic18CircleCrossedOut,
        text: lang.rejectedStatus,
        colorStatus: 'error',
    },
    information_requested: {
        svgName: icons.ic12CircleExclamation,
        text: lang.infoRequestedStatus,
        colorStatus: 'warning',
    },
}

// eslint-disable-next-line max-lines-per-function, complexity
function Level({ level, isCurrent, isTokenLoading, loadingLevel, onUpgrade, isDisabled, status, transition }: Props) {
    const intl = useIntl()
    const nextLevel = transition?.next_level

    const isShowUpgradeButton = () => {
        if (!onUpgrade) {
            return false
        }
        if (
            [KycStatuses.pending, KycStatuses.rejected].includes((status as KycStatuses) || '') &&
            nextLevel === level
        ) {
            return false
        }
        return true
    }

    const contactSupportLink = intl.formatMessage(lang.contactSupportLink)

    const isShowSupportButton = status === KycStatuses.rejected && nextLevel === level

    const isButtonDisabled =
        isDisabled ||
        ([KycStatuses.pending, KycStatuses.information_requested].includes((status as KycStatuses) || '') &&
            nextLevel !== level) ||
        (loadingLevel !== level && isTokenLoading)

    const upgradeButtonLangProps =
        status === KycStatuses.information_requested && nextLevel === level ? lang.submit : lang.upgrade

    return (
        <Card
            className={cn(styles.card, {
                [styles.currentCard]: isCurrent || nextLevel === level,
                [styles.notCurrentCard]: !isCurrent && nextLevel !== level,
            })}
            dataPointer={`verification_section_${level}`}
        >
            <div className={styles.titleWrap}>
                <FormattedHTMLMessage
                    id={`settings.identity.level.${level}.title`}
                    defaultMessage={`Title ${level}`}
                    className={styles.title}
                />
                {isCurrent && (
                    <div className={cn(styles.currentBadge, styles.badge)}>
                        <FormattedHTMLMessage {...lang.current} dataPointer="current_level" />
                        <AppSvg size={12} svgName={icons.ic18Check} />
                    </div>
                )}

                {status && kycStatusMapping[status] && nextLevel === level && (
                    <div className={cn(styles.badge, styles[kycStatusMapping[status].colorStatus])}>
                        <FormattedHTMLMessage {...kycStatusMapping[status].text} data-pointer={`status-${status}`} />
                        <AppSvg size={12} svgName={kycStatusMapping[status].svgName} />
                    </div>
                )}
            </div>
            <div className={styles.definitionRow}>
                <FormattedHTMLMessage {...lang.limit} />
                <FormattedHTMLMessage
                    id={`settings.identity.level.${level}.limit`}
                    defaultMessage={`Limit ${level}`}
                    className={styles.definitionValue}
                />
            </div>
            <div className={styles.definitionRow}>
                <FormattedHTMLMessage {...lang.wireTransferFiat} />
                <FormattedHTMLMessage
                    id={`settings.identity.level.${level}.wireTransferFiat`}
                    defaultMessage={`WireTransferFiat ${level}`}
                    className={styles.definitionValue}
                />
            </div>
            <div className={styles.definitionRow}>
                <FormattedHTMLMessage {...lang.buyWithCard} />
                <FormattedHTMLMessage
                    id={`settings.identity.level.${level}.buyWithCard`}
                    defaultMessage={`BuyWithCard ${level}`}
                    className={styles.definitionValue}
                />
            </div>
            {isShowUpgradeButton() && (
                <div className={styles.submit}>
                    <Button
                        dataPointer="upgrade_button"
                        isLoading={loadingLevel === level && isTokenLoading}
                        isDisabled={isButtonDisabled}
                        fullWidth
                        onClick={onUpgrade}
                        mode="primary"
                        size="m"
                    >
                        <FormattedHTMLMessage {...upgradeButtonLangProps} />
                    </Button>
                </div>
            )}

            {isShowSupportButton && (
                <div className={styles.submit}>
                    <Button
                        size="m"
                        fullWidth
                        role="anchor"
                        href={contactSupportLink}
                        onClick={e => {
                            e.preventDefault()
                        }}
                        dataPointer="support_link_button"
                    >
                        <FormattedHTMLMessage {...lang.contactSupport} />
                    </Button>
                </div>
            )}
        </Card>
    )
}

export default Level
